import { useState, useEffect, useMemo } from "react";
import { FileText, RefreshCw, FileDown, Loader2 } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { useCompanyContext } from "../../../context";
import { getReportsII } from "../../../api/wiki";
import TileReport from "./TileReportII";
import ReportPDF, { CONFIG, buildFileName } from "../Wiki/tiles/ReportPDF";

/* ── Theme ── */
const THEMES = {
  dark: {
    badgeBg: "rgba(14,165,233,0.10)",
    badgeBorder: "rgba(14,165,233,0.22)",
    badgeText: "#38bdf8",
    labelBg: "rgba(255,255,255,0.06)",
    labelBorder: "rgba(255,255,255,0.10)",
    labelText: "#a1a1aa",
    textSecondary: "#d4d4d8",
    textMuted: "#71717a",
    divider: "rgba(255,255,255,0.08)",
    pageBg: "#121C2E",
    cardBorder: "rgba(255,255,255,0.08)",
    accent: "#0EA5E9",
  },
  light: {
    badgeBg: "rgba(14,165,233,0.08)",
    badgeBorder: "rgba(14,165,233,0.20)",
    badgeText: "#0284c7",
    labelBg: "rgba(0,0,0,0.04)",
    labelBorder: "rgba(0,0,0,0.10)",
    labelText: "#71717a",
    textSecondary: "#3f3f46",
    textMuted: "#a1a1aa",
    divider: "rgba(0,0,0,0.08)",
    pageBg: "#e4eaf2",
    cardBorder: "rgba(0,0,0,0.08)",
    accent: "#2d3d52",
  },
};

const useReportTheme = () => {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains("dark")),
    );
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);
  return { ...(dark ? THEMES.dark : THEMES.light), isDark: dark };
};

/* ── Section grouping ── */
const sectionNum = (key) => {
  const m = String(key || "").match(/\d+/);
  return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
};

const groupSectionsAcrossReports = (reports) => {
  const bucket = new Map();
  (reports || []).forEach((report, rIdx) => {
    const versions = report?.versions;
    console.log(`[group] report ${rIdx}`, {
      report_id: report?.report_id,
      hasVersions: Array.isArray(versions),
      versionCount: versions?.length,
      firstVersionKeys: versions?.[0] ? Object.keys(versions[0]) : null,
      firstSectionField: versions?.[0]?.section,
    });
    (versions || []).forEach((section) => {
      const key = section?.section;
      if (!key) return;
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key).push({
        ...section,
        report_id: section.report_id || report.report_id,
      });
    });
  });
  for (const versions of bucket.values()) {
    versions.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }
  const grouped = [...bucket.entries()]
    .sort(([a], [b]) => sectionNum(a) - sectionNum(b))
    .map(([sectionKey, versions]) => ({ sectionKey, versions }));
  console.log("[group] result", {
    bucketKeys: [...bucket.keys()],
    sectionCount: grouped.length,
  });
  return grouped;
};

/* ── Skeleton ── */
const TileSkeleton = ({ delay = 0 }) => (
  <div
    className="rounded-xl border border-surfaceBorder bg-reortTileBg overflow-hidden"
    style={{ minHeight: 160, animationDelay: `${delay}ms` }}
  >
    <div className="px-5 pt-4 pb-3">
      <div className="h-4 w-2/5 rounded-md bg-innerBg animate-pulse mb-3" />
      <div className="flex gap-2">
        <div className="h-5 w-20 rounded-md bg-innerBg animate-pulse" />
        <div className="h-5 w-14 rounded bg-innerBg animate-pulse" />
      </div>
    </div>
    <div className="h-px mx-5 bg-innerBg" />
    <div className="px-5 pt-3 pb-4 flex flex-col gap-2.5">
      <div className="h-3 w-full rounded bg-innerBg animate-pulse" />
      <div className="h-3 w-5/6 rounded bg-innerBg animate-pulse" />
      <div className="h-3 w-4/6 rounded bg-innerBg animate-pulse" />
    </div>
  </div>
);

/* ── DefaultReportView ── */
const DefaultReportView = () => {
  const { currentCompany } = useCompanyContext();
  const t = useReportTheme();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedBySection, setSelectedBySection] = useState({});

  const companyName = currentCompany?.case || "";
  const sections = useMemo(
    () => groupSectionsAcrossReports(reports),
    [reports],
  );

  console.log("[DefaultReportView render]", {
    loading,
    error,
    reportsLen: reports.length,
    sectionsLen: sections.length,
    visible,
    companyName,
  });

  const loadReports = () => {
    console.log("[DefaultReportView] loadReports called");
    setLoading(true);
    setError(false);
    setVisible(false);
    setSelectedBySection({});
    getReportsII()
      .then((res) => {
        console.log("[DefaultReportView] getReportsII resolved", {
          dataLen: res?.data?.length,
          firstItemKeys: res?.data?.[0] ? Object.keys(res.data[0]) : null,
          raw: res,
        });
        setReports(res?.data || []);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setVisible(true)),
        );
      })
      .catch((err) => {
        console.error("[DefaultReportView] getReportsII rejected", err);
        setReports([]);
        setError(true);
      })
      .finally(() => {
        console.log("[DefaultReportView] loadReports finally");
        setLoading(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadReports();
  }, [companyName]);

  const handleSectionVersionChange = (sectionKey, idx) => {
    setSelectedBySection((prev) => ({ ...prev, [sectionKey]: idx }));
  };

  const handleExportAll = async () => {
    if (isExporting || sections.length === 0) return;
    setIsExporting(true);
    try {
      const reportsForPdf = sections
        .map(({ sectionKey, versions }) => {
          const idx = selectedBySection[sectionKey] ?? 0;
          const selected = versions[idx] || versions[0];
          if (!selected) return null;
          return {
            title: selected.section_title || sectionKey,
            target_type: sectionKey,
            versions: [selected],
          };
        })
        .filter(Boolean);

      const blob = await pdf(
        <ReportPDF reports={reportsForPdf} companyName={companyName} />,
      ).toBlob();

      const today = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildFileName({
        entityTitle: CONFIG.entityTitle,
        companyName,
        timestamp: today,
      });
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("[DefaultReportView] PDF export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    console.log("[DefaultReportView] → rendering LOADING");
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto px-8 pt-16 pb-8" style={{ maxWidth: 1000 }}>
          <div className="flex items-center justify-end mb-6">
            <div className="h-8 w-28 rounded-lg bg-innerBg animate-pulse" />
          </div>
          <div className="flex flex-col gap-4">
            {Array.from({ length: 6 }, (_, i) => (
              <TileSkeleton key={i} delay={i * 60} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log("[DefaultReportView] → rendering ERROR");
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 pt-14">
        <FileText
          className="w-8 h-8 text-zinc-300 dark:text-zinc-700"
          strokeWidth={1.5}
        />
        <p className="text-[13px] text-zinc-400 dark:text-zinc-600">
          Failed to load reports
        </p>
        <button
          onClick={loadReports}
          className="flex items-center gap-1.5 text-[12px] text-velBlue hover:underline"
        >
          <RefreshCw className="w-3 h-3" strokeWidth={2} />
          Try again
        </button>
      </div>
    );
  }

  if (sections.length === 0) {
    console.log("[DefaultReportView] → rendering EMPTY");
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 pt-14">
        <FileText
          className="w-8 h-8 text-zinc-300 dark:text-zinc-700"
          strokeWidth={1.5}
        />
        <p className="text-[14px] text-zinc-400 dark:text-zinc-600">
          No reports yet for {companyName || "this company"}.
        </p>
      </div>
    );
  }

  console.log("[DefaultReportView] → rendering POPULATED", {
    sectionCount: sections.length,
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto px-8 pt-16 pb-8" style={{ maxWidth: 1000 }}>
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={handleExportAll}
            disabled={isExporting}
            style={{
              background: t.isDark ? t.badgeBg : t.labelBg,
              border: `1px solid ${t.isDark ? t.badgeBorder : t.labelBorder}`,
              color: t.isDark ? t.badgeText : t.labelText,
            }}
            className="flex items-center gap-2 px-3 py-1.5 mt-4 rounded-lg text-[11px]
                       font-medium transition-opacity hover:opacity-70
                       active:opacity-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
            ) : (
              <FileDown className="h-3 w-3" strokeWidth={2} />
            )}
            {isExporting ? "Exporting…" : "Export PDF"}
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {sections.map(({ sectionKey, versions }, index) => (
            <div
              key={sectionKey}
              style={{
                transition: `opacity 0.4s ease-out ${Math.min(index * 55, 650)}ms, transform 0.4s ease-out ${Math.min(index * 55, 650)}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(10px)",
              }}
            >
              <TileReport
                sectionKey={sectionKey}
                versions={versions}
                t={t}
                selectedIdx={selectedBySection[sectionKey] ?? 0}
                onSelectVersion={(idx) =>
                  handleSectionVersionChange(sectionKey, idx)
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DefaultReportView;
